import { Capacitor } from '@capacitor/core';

import { setGraphQLTokenProvider } from '../lib/graphql-client';
import type { AppConfig } from '../types/config';
import type { AuthAdapter } from './AuthAdapter';
import { WebAuthAdapter } from './WebAuthAdapter';

let authAdapter: AuthAdapter | null = null;

/**
 * Initialize the shared AuthAdapter singleton.
 *
 * On native platforms (iOS / Android), dynamically imports MobileAuthAdapter
 * (keeping Capacitor native plugins out of the web bundle) then creates an
 * instance that uses Keychain storage and Bearer token transport, and wires it
 * up as the GraphQL token provider so all requests include an Authorization header.
 *
 * On web, creates a WebAuthAdapter that uses httpOnly cookie transport — the
 * browser attaches cookies automatically and no token provider is needed.
 *
 * Must be called after config.json is loaded (in ConfigContext, after initializeGraphQLClient).
 * Must be awaited — callers must not render auth-dependent children until the returned
 * Promise resolves, as getAuthAdapter() will throw if called before initialization completes.
 */
export async function initializeAuthAdapter(config: AppConfig): Promise<void> {
  // Idempotent: React StrictMode double-invokes effects; a second call must not create a new adapter instance and break BroadcastChannel identity.
  if (authAdapter !== null) return;
  if (Capacitor.isNativePlatform()) {
    if (!config.MOBILE_URL_SCHEME) {
      throw new Error('MOBILE_URL_SCHEME is required in config.json on native platforms');
    }
    // Dynamic import keeps MobileAuthAdapter and all Capacitor native plugins
    // (@capacitor/app, @capacitor/browser, capacitor-secure-storage-plugin) in a
    // separate chunk that web browsers never fetch or evaluate.
    const { MobileAuthAdapter } = await import('./MobileAuthAdapter');
    const mobile = new MobileAuthAdapter({
      baseUrl: config.MDVIP_API_BASE_URL,
      urlScheme: config.MOBILE_URL_SCHEME,
    });
    authAdapter = mobile;
    // Wire the Keychain token into every GraphQL request via the shared client.
    // Web never calls this — cookies handle auth there without JS token access.
    setGraphQLTokenProvider(() => mobile.getAccessToken());
  } else {
    authAdapter = new WebAuthAdapter({ baseUrl: config.MDVIP_API_BASE_URL });
    // No token provider for web — GraphQL client relies on credentials: 'include'.
  }
}

/**
 * Get the AuthAdapter singleton.
 * Throws if initializeAuthAdapter has not been called.
 */
export function getAuthAdapter(): AuthAdapter {
  if (!authAdapter) {
    throw new Error('AuthAdapter not initialized. Call initializeAuthAdapter first.');
  }
  return authAdapter;
}
